import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../lib/store'
import { LogOut, Calendar, DollarSign, Users, CheckCircle, Clock, XCircle, Home, Settings, Bell, Filter, Search, ArrowUpDown, ArrowUp, ArrowDown, Shield } from 'lucide-react'
import { toast } from '../lib/toast'
import BookingModal from './BookingModal'
import NewBookingModal from './NewBookingModal'
import AdminDashboard from './AdminDashboard'
import UserManagement from './UserManagement'
import ServicesManagement from './ServicesManagement'
import ProviderDashboard from './ProviderDashboard'
import ProfileModal from './ProfileModal'
import ThemeToggle from './ThemeToggle'
import type { Booking } from '../types'

export default function Dashboard() {
  const { bookings, loading, user, setUser, setBookings } = useAppStore()
  const navigate = useNavigate()
  
  // Debug logging to track user role changes
  React.useEffect(() => {
    console.log('Dashboard - User role changed:', user?.role, user?.email)
    console.log('Dashboard - Full user object:', user)
    console.log('Dashboard - localStorage user:', JSON.parse(localStorage.getItem('user') || '{}'))
  }, [user?.role, user?.email, user])
  
  // Additional debugging for routing
  React.useEffect(() => {
    console.log('Dashboard - Routing check:')
    console.log('  - User exists:', !!user)
    console.log('  - User role:', user?.role)
    console.log('  - Is admin:', user?.role === 'admin')
    console.log('  - Is provider:', user?.role === 'provider') 
    console.log('  - Is client:', user?.role === 'client')
  }, [user])
  const [selectedBooking, setSelectedBooking] = React.useState<Booking | null>(null)
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false)
  const [isNewBookingModalOpen, setIsNewBookingModalOpen] = React.useState(false)
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<Booking['status'] | 'all'>('all')
  const [currentView, setCurrentView] = React.useState<'bookings' | 'users' | 'services' | 'analytics'>('bookings')
  const [sortBy, setSortBy] = React.useState<'date' | 'status' | 'amount' | 'service_name'>('date')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')

  const handleLogout = () => {
    setUser(null)
    navigate('/')
  }

  const handleGoHome = () => {
    navigate('/')
  }

  const handleOpenProfile = () => {
    setIsProfileModalOpen(true)
  }

  const handleCloseProfile = () => {
    setIsProfileModalOpen(false)
  }

  const handleCreateBooking = () => {
    setIsNewBookingModalOpen(true)
  }

  const handleBookingClick = (booking: Booking) => {
    setSelectedBooking(booking)
    setIsBookingModalOpen(true)
  }

  const handleUpdateBooking = (updatedBooking: Booking) => {
    setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b))
  }

  const handleDeleteBooking = (bookingId: string) => {
    setBookings(bookings.filter(b => b.id !== bookingId))
  }

  const handleCreateBookingSubmit = (newBookingData: Omit<Booking, 'id'>) => {
    const newBooking: Booking = {
      ...newBookingData,
      id: Date.now().toString()
    }
    setBookings([...bookings, newBooking])
  }

  // Role-based data filtering
  const getRoleFilteredBookings = () => {
    if (user?.role === 'admin') {
      // Real admins see only real bookings (no demo data)
      // Demo admins can see sample data for testing
      const demoAdminEmails = ['admin@demo.com', 'test@admin.com'];
      
      if (demoAdminEmails.includes(user.email)) {
        return bookings; // Demo admin sees all data
      } else {
        // Real admin sees only real bookings (no 2024 demo data)
        return bookings.filter(booking => !booking.date.startsWith('2024'));
      }
    }
    if (user?.role === 'provider') {
      // Providers see bookings assigned to them (mock logic - in real app would have provider_id field)
      return bookings.filter(booking => 
        booking.status !== 'cancelled' // Active bookings only for providers
      )
    }
    if (user?.role === 'client') {
      // Clients see only their own bookings (mock logic - in real app would have client_id field)
      return bookings.filter(booking => 
        parseInt(booking.id) % 3 === 1 // Mock: every 3rd booking belongs to current client
      )
    }
    return bookings
  }

  // Sort functionality
  const sortBookings = (bookingsToSort: Booking[]) => {
    const sorted = [...bookingsToSort].sort((a, b) => {
      let comparison = 0
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime()
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'amount':
          comparison = a.amount - b.amount
          break
        case 'service_name':
          comparison = a.service_name.localeCompare(b.service_name)
          break
        default:
          comparison = 0
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    return sorted
  }

  // Filter and sort bookings
  const filteredBookings = sortBookings(
    getRoleFilteredBookings().filter(booking => {
      const matchesSearch = booking.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           booking.date.includes(searchTerm) ||
                           booking.status.toLowerCase().includes(searchTerm)
      const matchesStatus = statusFilter === 'all' || booking.status === statusFilter
      return matchesSearch && matchesStatus
    })
  )

  // Calculate stats
  const stats = {
    total: filteredBookings.length,
    confirmed: filteredBookings.filter(b => b.status === 'confirmed').length,
    pending: filteredBookings.filter(b => b.status === 'pending').length,
    completed: filteredBookings.filter(b => b.status === 'completed').length,
    totalEarnings: filteredBookings.reduce((sum, b) => sum + b.amount, 0)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-blue-500" />
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <Clock className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Render provider-specific dashboard
  if (user?.role === 'provider') {
    return <ProviderDashboard />
  }

  // Render admin-specific dashboard
  if (user?.role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
        {/* Admin Header - Using Landing Page Navigation */}
        <header className="fixed top-0 w-full bg-white/95 dark:bg-dark-card/95 backdrop-blur-sm border-b border-gray-200 dark:border-dark-border z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <Shield className="w-8 h-8 text-orange-500" />
                <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">SmartClean</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <ThemeToggle />
                <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Notifications">
                  <Bell className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleOpenProfile}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Edit Profile"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 py-8">
          {/* Admin Navigation */}
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setCurrentView('bookings')}
                className={`py-4 px-2 border-b-2 transition-colors font-bold ${
                  currentView === 'bookings' 
                    ? 'border-emerald-600 text-emerald-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                Bookings
              </button>
              <button
                onClick={() => setCurrentView('users')}
                className={`py-4 px-2 border-b-2 transition-colors font-bold ${
                  currentView === 'users' 
                    ? 'border-emerald-600 text-emerald-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setCurrentView('services')}
                className={`py-4 px-2 border-b-2 transition-colors font-bold ${
                  currentView === 'services' 
                    ? 'border-emerald-600 text-emerald-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                Services
              </button>
              <button
                onClick={() => setCurrentView('analytics')}
                className={`py-4 px-2 border-b-2 transition-colors font-bold ${
                  currentView === 'analytics' 
                    ? 'border-emerald-600 text-emerald-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                Analytics
              </button>
            </div>
          </div>

          {/* Admin Content */}
          {currentView === 'bookings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">All Bookings</h2>
              
              {/* Search and Filters */}
              <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:flex-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full md:w-full pl-8 md:pl-10 pr-2 md:pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-3 h-3 md:w-4 md:h-4 text-gray-500 dark:text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as Booking['status'] | 'all')}
                      className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                    >
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button 
                      onClick={handleCreateBooking}
                      className="bg-green-700 text-white px-3 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      + New
                    </button>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'date' | 'status' | 'amount' | 'service_name')}
                      className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                    >
                      <option value="date">Sort by Date</option>
                      <option value="status">Sort by Status</option>
                      <option value="amount">Sort by Amount</option>
                      <option value="service_name">Sort by Service</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    >
                      {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4 text-gray-600 dark:text-gray-400" /> : <ArrowDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Bookings Grid */}
              <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">All Bookings ({filteredBookings.length})</h2>
                  </div>
                  
                  <div className="p-3 md:p-6">
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                      {filteredBookings.map((booking) => (
                        <div 
                          key={booking.id} 
                          className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleBookingClick(booking)}
                        >
                          <div className="flex justify-between items-start mb-2 md:mb-4">
                            <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                              {booking.service_name}
                            </h3>
                            <div className={`flex items-center space-x-1 px-1 md:px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                              {getStatusIcon(booking.status)}
                              <span className="hidden md:inline">{booking.status}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-1 md:space-y-2 mb-2 md:mb-4">
                            <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                              <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                              <span className="text-xs md:text-sm">{booking.date}</span>
                            </div>
                            <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                              <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                              <span className="text-xs md:text-sm">{booking.time}</span>
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <p className="text-base md:text-2xl font-bold text-emerald-500">
                              ${booking.amount}
                            </p>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation()
                                handleBookingClick(booking)
                              }}
                              className="text-xs md:text-sm font-medium text-orange-600 dark:text-emerald-600 hover:text-orange-700 dark:hover:text-emerald-700 transition-colors"
                            >
                              <span className="hidden md:inline">Manage →</span>
                              <span className="md:hidden">→</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentView === 'users' && <UserManagement />}
          {currentView === 'services' && <ServicesManagement />}
          {currentView === 'analytics' && <AdminDashboard />}
        </main>

        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={handleCloseProfile}
        />

        {/* Modals */}
        <BookingModal
          booking={selectedBooking}
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          onUpdate={handleUpdateBooking}
          onDelete={handleDeleteBooking}
        />

        <NewBookingModal
          isOpen={isNewBookingModalOpen}
          onClose={() => setIsNewBookingModalOpen(false)}
          onCreate={handleCreateBookingSubmit}
        />
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600 dark:text-dark-text-primary">Loading...</div>
        </div>
      </div>
    )
  }

  if (user?.role === 'client') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
        {/* Header */}
        <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={handleGoHome}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Go to Home"
                >
                  <Home className="w-6 h-6 text-orange-500" />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">Client Dashboard</h1>
                  <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Welcome back, {user?.email}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <ThemeToggle />
                <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Notifications">
                  <Bell className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleOpenProfile}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Edit Profile"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-8">
            <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
              <div className="flex flex-col items-center justify-center">
                <Calendar className="w-3 h-3 md:w-8 md:h-8 text-blue-500 mb-1" />
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Total</p>
                <p className="text-sm md:text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{stats.total}</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
              <div className="flex flex-col items-center justify-center">
                <CheckCircle className="w-3 h-3 md:w-8 md:h-8 text-green-500 mb-1" />
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Confirmed</p>
                <p className="text-sm md:text-2xl font-bold text-green-600">{stats.confirmed}</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
              <div className="flex flex-col items-center justify-center">
                <Clock className="w-3 h-3 md:w-8 md:h-8 text-yellow-500 mb-1" />
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Pending</p>
                <p className="text-sm md:text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
              <div className="flex flex-col items-center justify-center">
                <DollarSign className="w-3 h-3 md:w-8 md:h-8 text-emerald-500 mb-1" />
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Earnings</p>
                <p className="text-sm md:text-2xl font-bold text-emerald-600">${stats.totalEarnings}</p>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5" />
                <input
                  type="text"
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 md:pl-10 pr-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm md:text-base"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as Booking['status'] | 'all')}
                  className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                
                <button
                  onClick={() => setIsNewBookingModalOpen(true)}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <span className="hidden md:inline">+ New</span>
                  <span className="md:hidden">+</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bookings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                onClick={() => handleBookingClick(booking)}
                className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-4 md:p-6 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                    {booking.service_name}
                  </h3>
                  <div className={`flex items-center space-x-1 px-1 md:px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                    {getStatusIcon(booking.status)}
                    <span className="hidden md:inline">{booking.status}</span>
                  </div>
                </div>
                
                <div className="space-y-1 md:space-y-2 mb-2 md:mb-4">
                  <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                    <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    <span className="text-xs md:text-sm">{booking.date}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    <span className="text-xs md:text-sm">{booking.time}</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-base md:text-2xl font-bold text-emerald-500">
                    ${booking.amount}
                  </p>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      handleBookingClick(booking)
                    }}
                    className="text-xs md:text-sm font-medium text-orange-600 dark:text-emerald-600 hover:text-orange-700 dark:hover:text-emerald-700 transition-colors"
                  >
                    <span className="hidden md:inline">Manage →</span>
                    <span className="md:hidden">→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Modals */}
        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={handleCloseProfile}
        />

        <BookingModal
          booking={selectedBooking}
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          onUpdate={handleUpdateBooking}
          onDelete={handleDeleteBooking}
        />

        <NewBookingModal
          isOpen={isNewBookingModalOpen}
          onClose={() => setIsNewBookingModalOpen(false)}
          onCreate={handleCreateBookingSubmit}
        />
      </div>
    )
  }

  const getRoleGreeting = () => {
    switch (user?.role) {
      case 'admin':
        return 'Admin Dashboard'
      case 'provider':
        return 'Provider Dashboard'
      case 'client':
        return 'Client Dashboard'
      default:
        return 'Dashboard'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleGoHome}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Go to Home"
              >
                <Home className="w-6 h-6 text-orange-500" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">{getRoleGreeting()}</h1>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Welcome back, {user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Notifications">
                <Bell className="w-5 h-5" />
              </button>
              <button 
                onClick={handleOpenProfile}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Edit Profile"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-lg hover:bg-orange-200 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-8">
          <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex flex-col items-center justify-center">
              <Calendar className="w-3 h-3 md:w-8 md:h-8 text-blue-500 mb-1" />
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Total</p>
              <p className="text-sm md:text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{stats.total}</p>
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex flex-col items-center justify-center">
              <CheckCircle className="w-3 h-3 md:w-8 md:h-8 text-green-500 mb-1" />
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Confirmed</p>
              <p className="text-sm md:text-2xl font-bold text-green-600">{stats.confirmed}</p>
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex flex-col items-center justify-center">
              <Clock className="w-3 h-3 md:w-8 md:h-8 text-yellow-500 mb-1" />
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Pending</p>
              <p className="text-sm md:text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex flex-col items-center justify-center">
              <DollarSign className="w-3 h-3 md:w-8 md:h-8 text-blue-500 mb-1" />
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Earnings</p>
              <p className="text-sm md:text-2xl font-bold text-blue-600">${stats.totalEarnings}</p>
            </div>
          </div>
        </div>

        {/* Bookings Grid */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="px-3 md:px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Recent Bookings</h2>
              <button 
                onClick={handleCreateBooking}
                className="hidden md:block bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-medium"
              >
                + New Booking
              </button>
            </div>
            
            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row gap-2 md:gap-4 mt-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 md:pl-10 pr-2 md:pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Filter className="w-3 h-3 md:w-4 md:h-4 text-gray-500 dark:text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as Booking['status'] | 'all')}
                  className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <button 
                onClick={handleCreateBooking}
                className="md:hidden bg-green-700 text-white px-3 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-medium whitespace-nowrap"
              >
                + New
              </button>
            </div>
          </div>
          
          <div className="p-3 md:p-6">
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {filteredBookings.map((booking) => (
                <div 
                  key={booking.id} 
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleBookingClick(booking)}
                >
                  <div className="flex justify-between items-start mb-2 md:mb-4">
                    <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                      {booking.service_name}
                    </h3>
                    <div className={`flex items-center space-x-1 px-1 md:px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                      {getStatusIcon(booking.status)}
                      <span className="hidden md:inline">{booking.status}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-1 md:space-y-2 mb-2 md:mb-4">
                    <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                      <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                      <span className="text-xs md:text-sm">{booking.date}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-dark-text-secondary">
                      <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                      <span className="text-xs md:text-sm">{booking.time}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <p className="text-base md:text-2xl font-bold text-emerald-500">
                      ${booking.amount}
                    </p>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation()
                        handleBookingClick(booking)
                      }}
                      className="text-xs md:text-sm font-medium text-orange-600 dark:text-emerald-600 hover:text-orange-700 dark:hover:text-emerald-700 transition-colors"
                    >
                      <span className="hidden md:inline">Manage →</span>
                      <span className="md:hidden">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfile}
      />

      <BookingModal
        booking={selectedBooking}
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        onUpdate={handleUpdateBooking}
        onDelete={handleDeleteBooking}
      />

      <NewBookingModal
        isOpen={isNewBookingModalOpen}
        onClose={() => setIsNewBookingModalOpen(false)}
        onCreate={handleCreateBookingSubmit}
      />
    </div>
  )
}
